import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import adminAxios from './adminAxios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, UserCheck, Search, Check, X, Copy, User, KeyRound, Clock, CheckCheck, XCircle } from 'lucide-react';

const StatusPill = ({ status }) => {
  const map = {
    pending: { text: 'Pendente', cls: 'bg-amber-100 text-amber-700', icon: Clock },
    approved: { text: 'Aprovado', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCheck },
    rejected: { text: 'Rejeitado', cls: 'bg-red-100 text-red-700', icon: XCircle },
  };
  const conf = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${conf.cls}`}>
      <conf.icon className="w-3 h-3" /> {conf.text}
    </span>
  );
};

const AdminPersonalApprovals = () => {
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approvedInfo, setApprovedInfo] = useState(null); // { code, email }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (search) params.append('search', search);
      const res = await adminAxios.get(`${API}/admin/personal-approvals?${params}`);
      setItems(res.data.registrations || []);
      setCounts(res.data.counts || {});
    } catch { toast.error('Erro ao carregar cadastros'); }
    finally { setLoading(false); }
  }, [filter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approve = async (reg_id) => {
    if (!window.confirm('Aprovar este cadastro? Um código de ativação será gerado.')) return;
    setApproving(reg_id);
    try {
      const res = await adminAxios.post(`${API}/admin/personal-approvals/${reg_id}/approve`, {});
      setApprovedInfo({ code: res.data.activation_code, email: res.data.user_email });
      toast.success('Cadastro aprovado!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
    finally { setApproving(null); }
  };

  const submitReject = async () => {
    if (!rejectDialog) return;
    try {
      await adminAxios.post(`${API}/admin/personal-approvals/${rejectDialog.reg_id}/reject`, { reason: rejectReason });
      toast.success('Cadastro rejeitado');
      setRejectDialog(null); setRejectReason('');
      fetchData();
    } catch { toast.error('Erro ao rejeitar'); }
  };

  const copyCode = () => {
    if (!approvedInfo?.code) return;
    navigator.clipboard.writeText(approvedInfo.code);
    toast.success('Código copiado!');
  };

  return (
    <div className="space-y-6" data-testid="admin-personal-approvals">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <UserCheck className="w-6 h-6 text-emerald-600" />Aprovação de Cadastros
          </h2>
          <p className="text-sm text-slate-500">Gerenciar cadastros de pessoas físicas (Vital para Você)</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: 'pending', label: 'Pendentes', count: counts.pending },
          { key: 'approved', label: 'Aprovados', count: counts.approved },
          { key: 'rejected', label: 'Rejeitados', count: counts.rejected },
          { key: 'all', label: 'Todos' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${filter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label} {f.count != null && <span className="ml-1 opacity-70">({f.count})</span>}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input placeholder="Buscar por nome, CPF ou e-mail" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 text-sm" data-testid="search-input" />
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-slate-400 text-sm">Nenhum cadastro encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500">Nome</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500">CPF</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500">E-mail</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500 hidden md:table-cell">Telefone</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500 hidden lg:table-cell">Cidade</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500 hidden lg:table-cell">Data</th>
                    <th className="px-4 py-3 text-left text-xs uppercase font-semibold text-slate-500">Status</th>
                    <th className="px-4 py-3 text-right text-xs uppercase font-semibold text-slate-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.reg_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`row-${item.reg_id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{item.cpf}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{item.email}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs hidden md:table-cell">{item.cellphone}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs hidden lg:table-cell">{item.city}/{item.state}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs hidden lg:table-cell">{item.created_at ? new Date(item.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="px-4 py-3"><StatusPill status={item.status} /></td>
                      <td className="px-4 py-3 text-right space-x-1">
                        {item.status === 'pending' && (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" onClick={() => approve(item.reg_id)} disabled={approving === item.reg_id} data-testid={`approve-${item.reg_id}`}>
                              {approving === item.reg_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Aprovar</>}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRejectDialog(item)} data-testid={`reject-${item.reg_id}`}>
                              <X className="w-3 h-3 mr-1" />Rejeitar
                            </Button>
                          </>
                        )}
                        {item.status === 'approved' && item.activation_code && (
                          <button onClick={() => { navigator.clipboard.writeText(item.activation_code); toast.success('Código copiado!'); }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-900 text-emerald-400 rounded font-mono text-xs">
                            <KeyRound className="w-3 h-3" />{item.activation_code}
                          </button>
                        )}
                        {item.status === 'rejected' && item.rejection_reason && (
                          <span className="text-xs text-slate-400 italic max-w-[200px] truncate inline-block" title={item.rejection_reason}>{item.rejection_reason}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approved success modal */}
      {approvedInfo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setApprovedInfo(null)} data-testid="approved-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Cadastro Aprovado!</h3>
              <p className="text-sm text-slate-500 mt-1">Compartilhe o código de ativação com o usuário</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-xs uppercase text-slate-500 mb-1">Usuário</p>
              <p className="text-sm text-slate-700 flex items-center justify-center gap-1"><User className="w-4 h-4" />{approvedInfo.email}</p>
            </div>

            <div className="bg-slate-900 rounded-xl p-5 text-center">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Código de Ativação</p>
              <p className="text-3xl font-black text-emerald-400 tracking-[0.3em] font-mono" data-testid="activation-code">{approvedInfo.code}</p>
            </div>

            <Button onClick={copyCode} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-code-btn">
              <Copy className="w-4 h-4 mr-2" />Copiar Código
            </Button>
            <Button variant="ghost" onClick={() => setApprovedInfo(null)} className="w-full text-slate-500">Fechar</Button>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {rejectDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRejectDialog(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Rejeitar cadastro</h3>
            <p className="text-sm text-slate-500">Rejeitar cadastro de <strong>{rejectDialog.name}</strong>?</p>
            <div>
              <label className="text-xs text-slate-600 font-medium mb-1 block">Motivo (opcional)</label>
              <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Ex: CPF já cadastrado" data-testid="reject-reason" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setRejectDialog(null); setRejectReason(''); }} className="flex-1">Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={submitReject} data-testid="confirm-reject-btn">Rejeitar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPersonalApprovals;
